import type { Constant, Formula, Session } from './types';

/**
 * The formula and constant cheat sheet, built automatically from the reference
 * sections of every imported session.
 *
 * De-duplication is by id, as specified. On top of that, entries that say
 * exactly the same thing under different ids are collapsed too — a class and
 * its chapter review both defining Molarity is the normal case, and printing it
 * twice would be noise. The chapter-review copy is the one kept, matching how
 * cumulative review resolves the same clash.
 */

export interface CheatSheetGroup {
  topic: string;
  formulas: Formula[];
}

export interface ReferenceIssue {
  kind: 'duplicate-id' | 'repeated-entry' | 'empty-topic' | 'odd-constant';
  message: string;
}

export interface CheatSheet {
  groups: CheatSheetGroup[];
  constants: Constant[];
  formulaCount: number;
  constantCount: number;
  issues: ReferenceIssue[];
}

const UNGROUPED = 'Other';

/** For names: case and run-length of spaces do not matter. */
function normalizeName(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * For expressions: spacing is meaningless, so it all goes. "M = n / V" and
 * "M = n/V" are the same formula written by two different chats.
 */
function normalizeExpr(text: string): string {
  return text.toLowerCase().replace(/\s+/g, '');
}

/** Keeps one row per id, preferring the most recently updated. */
function byId<T extends { id: string; updatedAt: number; deleted: boolean }>(
  rows: T[],
): { kept: T[]; removed: number } {
  const best = new Map<string, T>();
  let removed = 0;
  for (const row of rows) {
    if (row.deleted) continue;
    const current = best.get(row.id);
    if (!current) {
      best.set(row.id, row);
      continue;
    }
    removed += 1;
    if (row.updatedAt > current.updatedAt) best.set(row.id, row);
  }
  return { kept: [...best.values()], removed };
}

export function buildCheatSheet(
  formulas: Formula[],
  constants: Constant[],
  sessions: Session[],
): CheatSheet {
  const issues: ReferenceIssue[] = [];
  const chapterSessions = new Set(
    sessions.filter((s) => s.kind === 'chapter' && !s.deleted).map((s) => s.id),
  );

  const f = byId(formulas);
  const c = byId(constants);
  if (f.removed + c.removed > 0) {
    issues.push({
      kind: 'duplicate-id',
      message: `${f.removed + c.removed} entries shared an id with another; the newest was kept.`,
    });
  }

  // Chapter-review entries first, so they win an identical-content clash.
  const rank = (sessionId: string) => (chapterSessions.has(sessionId) ? 0 : 1);

  const orderedFormulas = f.kept.slice().sort((a, b) => rank(a.sessionId) - rank(b.sessionId));
  const seenFormulas = new Map<string, Formula>();
  let repeatedFormulas = 0;
  for (const formula of orderedFormulas) {
    const key = `${normalizeName(formula.name)}|${normalizeExpr(formula.expr)}`;
    if (seenFormulas.has(key)) {
      repeatedFormulas += 1;
      continue;
    }
    seenFormulas.set(key, formula);
  }

  const orderedConstants = c.kept.slice().sort((a, b) => rank(a.sessionId) - rank(b.sessionId));
  const seenConstants = new Map<string, Constant>();
  let repeatedConstants = 0;
  for (const constant of orderedConstants) {
    const key = `${normalizeName(constant.name)}|${normalizeExpr(constant.symbol)}|${normalizeExpr(constant.value)}`;
    if (seenConstants.has(key)) {
      repeatedConstants += 1;
      continue;
    }
    seenConstants.set(key, constant);
  }

  if (repeatedFormulas + repeatedConstants > 0) {
    issues.push({
      kind: 'repeated-entry',
      message: `${repeatedFormulas + repeatedConstants} entries repeated one already listed; the chapter-review wording was kept.`,
    });
  }

  const keptFormulas = [...seenFormulas.values()];
  const keptConstants = [...seenConstants.values()].sort((a, b) => a.name.localeCompare(b.name));

  if (keptFormulas.some((x) => !x.topic.trim())) {
    issues.push({ kind: 'empty-topic', message: 'Some formulas have no topic and are listed under "Other".' });
  }

  const odd = keptConstants.filter((x) => !looksNumeric(x.value));
  if (odd.length > 0) {
    issues.push({
      kind: 'odd-constant',
      message: `${odd.length} constant${odd.length === 1 ? ' has' : 's have'} a value that is not a plain number (${odd
        .slice(0, 3)
        .map((x) => x.name)
        .join(', ')}). Worth a check.`,
    });
  }

  const byTopic = new Map<string, Formula[]>();
  for (const formula of keptFormulas) {
    const topic = formula.topic.trim() || UNGROUPED;
    const list = byTopic.get(topic) ?? [];
    list.push(formula);
    byTopic.set(topic, list);
  }

  const groups: CheatSheetGroup[] = [...byTopic.entries()]
    .map(([topic, list]) => ({
      topic,
      formulas: list.slice().sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => {
      // "Other" always sits at the end; everything else alphabetically.
      if (a.topic === UNGROUPED) return 1;
      if (b.topic === UNGROUPED) return -1;
      return a.topic.localeCompare(b.topic);
    });

  return {
    groups,
    constants: keptConstants,
    formulaCount: keptFormulas.length,
    constantCount: keptConstants.length,
    issues,
  };
}

/** Accepts 6.022e23, 1.66054e-24, 6.022 x 10^23, 273.15, -40, 1/12 and the like. */
export function looksNumeric(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  // A simple fraction, as in 1/12.
  if (/^-?\d+(\.\d+)?\s*\/\s*-?\d+(\.\d+)?$/.test(trimmed)) return true;
  // "6.022 x 10^23" means the same as "6.022e23". Only a 10 that follows a
  // multiplication sign is an exponent base: replacing any 10 at all would eat
  // the one inside a mantissa such as 9.10938e-28 and reject a good number.
  const normalised = trimmed.replace(/\s*[×x*]\s*10\s*\^?\s*(-?\d+)\s*$/, 'e$1');
  return /^-?\d*\.?\d+(\s*[eE]\s*-?\d+)?$/.test(normalised);
}
